import { Paperclip, X } from "lucide-react"
import { useEffect, useState } from "react"

import { type Borrador, resumirSenal } from "@/componentes/soporte/guion"
import type { Miga, Senal } from "@/lib/bitacora"

/**
 * Los dos pasos que enseñan la evidencia: elegirla y revisarla.
 *
 * ⚠ **Nada viaja sin que se haya visto antes.** La bitácora recoge rutas, códigos y
 * a veces los valores que se escribieron en un formulario; enviarlos sin enseñarlos
 * sería recogerlos a espaldas de quien reporta. Poder mirarlo es lo que hace
 * legítimo recogerlo — y de paso, quien ve su propio recorrido a veces recuerda el
 * paso que se le había olvidado contar.
 */

export function PasoEvidencia({
  capturadas,
  borrador,
  onCambiar,
}: {
  capturadas: Senal[]
  borrador: Borrador
  onCambiar: (b: Borrador) => void
}) {
  function alternar(i: number) {
    const marcadas = borrador.marcadas.includes(i)
      ? borrador.marcadas.filter((x) => x !== i)
      : [...borrador.marcadas, i]
    onCambiar({ ...borrador, marcadas })
  }

  return (
    <div>
      {capturadas.length === 0 ? (
        <p className="superficie p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
          El navegador no registró ningún error. Eso <strong>no</strong> significa que no haya
          fallo: un total que no cuadra, una cifra desalineada o un botón que no responde no dejan
          rastro. Sigue adelante y cuéntalo con tus palabras.
        </p>
      ) : (
        <ul className="space-y-2">
          {capturadas.map((s, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: una señal no tiene id y su posición ES su identidad
            <li key={`${s.momento}-${i}`}>
              <label className="flex cursor-pointer items-start gap-2 superficie p-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={borrador.marcadas.includes(i)}
                  onChange={() => alternar(i)}
                />
                <span className="flex-1">
                  {resumirSenal(s)}
                  <span className="block text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                    {s.momento.slice(11, 19)}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm underline-offset-2 hover:underline">
            <Paperclip size={14} aria-hidden={true} />
            {borrador.captura === null ? "Adjuntar una captura" : "Cambiar la captura"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              data-testid="captura-reporte"
              onChange={(e) => onCambiar({ ...borrador, captura: e.target.files?.[0] ?? null })}
            />
          </label>
          {/* ⚠ **El atajo se ANUNCIA.** Un atajo que no está escrito en ningún sitio
              no existe: quien recorta con Win+Shift+S no adivina que puede pegarlo
              aquí, y acaba guardando el archivo para luego buscarlo. */}
          <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
            o pégala con <kbd className="font-mono">Ctrl</kbd>+<kbd className="font-mono">V</kbd>
          </span>
        </div>

        {borrador.captura !== null && (
          <VistaPreviaCaptura
            archivo={borrador.captura}
            onQuitar={() => onCambiar({ ...borrador, captura: null })}
          />
        )}

        {/* ⚠ Se avisa de lo que una captura arrastra. Es lo que más ayuda a dev y lo
            que más expone: sale TODO lo que hubiera en pantalla, de este cliente y
            de los demás. */}
        <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
          Ojo: una captura envía todo lo que se vea en pantalla, incluidos datos de otros clientes.
          Si hay algo que no deba salir, tápalo antes o no adjuntes nada.
        </p>
      </div>
    </div>
  )
}

const NOMBRE_MIGA = {
  CLIC: "Pulsó",
  RUTA: "Fue a",
  ENVIO: "Envió",
  ESCRITURA: "Escribió en",
}

export function PasoRevision({
  capturadas,
  recorrido,
  borrador,
  vistaAlReportar,
}: {
  capturadas: Senal[]
  recorrido: Miga[]
  borrador: Borrador
  vistaAlReportar: string
}) {
  const elegidas = borrador.marcadas.map((i) => capturadas[i]).filter(Boolean) as Senal[]

  return (
    <div className="superficie p-3 text-sm">
      <Dato titulo="Título" valor={borrador.titulo} />
      <Dato titulo="Pantalla" valor={borrador.vista} />
      {borrador.vista !== vistaAlReportar && (
        <Dato titulo="Reportado desde" valor={vistaAlReportar} />
      )}
      <Dato titulo="Qué intentabas" valor={borrador.queIntentaba} />
      <Dato titulo="Qué pasó" valor={borrador.quePaso} />

      <p className="mt-3 mb-1 text-xs font-medium uppercase" style={{ color: "var(--fg-muted)" }}>
        Errores que marcaste ({elegidas.length})
      </p>
      {elegidas.length === 0 ? (
        <p style={{ color: "var(--fg-muted)" }}>Ninguno.</p>
      ) : (
        <ul className="ml-4 list-disc">
          {elegidas.map((s, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: una señal no tiene id y su posición ES su identidad
            <li key={`${s.momento}-${i}`}>{resumirSenal(s)}</li>
          ))}
        </ul>
      )}

      {/* ⚠ El recorrido va SIEMPRE y por eso se enseña siempre: es lo que permite
          reproducir. Y aquí se ve que son etiquetas de controles, nunca lo tecleado. */}
      <p className="mt-3 mb-1 text-xs font-medium uppercase" style={{ color: "var(--fg-muted)" }}>
        Tus últimos pasos ({recorrido.length})
      </p>
      {recorrido.length === 0 ? (
        <p style={{ color: "var(--fg-muted)" }}>Sin registrar.</p>
      ) : (
        <ol className="ml-4 list-decimal">
          {recorrido.slice(-12).map((m, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: una miga no tiene id y su posición ES su identidad
            <li key={`${m.momento}-${i}`}>
              {NOMBRE_MIGA[m.tipo]} «{m.etiqueta}»
            </li>
          ))}
        </ol>
      )}
      <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
        Sólo se guarda el nombre de cada botón o campo, nunca lo que escribiste dentro.
      </p>

      {borrador.captura !== null && (
        <p className="mt-3 text-xs" style={{ color: "var(--fg-muted)" }}>
          Y la captura «{borrador.captura.name}».
        </p>
      )}
    </div>
  )
}

function Dato({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <p className="mb-2">
      <span className="block text-xs font-medium uppercase" style={{ color: "var(--fg-muted)" }}>
        {titulo}
      </span>
      {valor}
    </p>
  )
}

/**
 * Lo que se pegó, para poder mirarlo.
 *
 * ⚠ **Con Ctrl+V no se ve lo que se adjunta, y aquí eso importa más que en un
 * formulario normal.** El aviso de arriba dice que una captura arrastra todo lo que
 * hubiera en pantalla; sin vista previa, cumplirlo depende de acordarse de qué había
 * en el portapapeles. Con ella se comprueba antes de enviar, que es de lo que va el
 * aviso.
 *
 * ⚠ **El objeto se revoca al cambiar de archivo.** Si no, cada pegado deja un blob
 * colgando en memoria mientras la pestaña siga abierta — y pegar varias veces
 * seguidas hasta dar con la captura buena es justo lo que hace la gente.
 */
function VistaPreviaCaptura({ archivo, onQuitar }: { archivo: File; onQuitar: () => void }) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    const objeto = URL.createObjectURL(archivo)
    setUrl(objeto)
    return () => URL.revokeObjectURL(objeto)
  }, [archivo])

  return (
    <div className="mt-2 superficie p-2" data-testid="vista-previa-captura">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex-1 truncate text-xs" style={{ color: "var(--fg-muted)" }}>
          {archivo.name} · {Math.round(archivo.size / 1024)} KB
        </span>
        <button
          type="button"
          onClick={onQuitar}
          data-testid="quitar-captura"
          aria-label="Quitar la captura"
          className="boton-secundario h-7 w-7 justify-center p-0"
        >
          <X size={14} aria-hidden={true} />
        </button>
      </div>
      {url !== null && (
        <img
          src={url}
          alt="Vista previa de la captura adjunta"
          className="max-h-48 w-full rounded-[var(--radius-control)] object-contain"
          style={{ backgroundColor: "var(--surface-elevated)" }}
        />
      )}
    </div>
  )
}
